import React, { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { cn } from '@bem-react/classname'
import { useNavigate } from 'react-router-dom'

import { Memo } from '@/hoc/Memo'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'
import {
  connectedReselleridSelector,
  roleSelector,
  userRoleSelector,
} from './store/accont.selector'
import { resellersSelector } from './store/resellers.selector'
import { getResellersThunk } from './store/resellers.slice'
import useBackendError from '@/hooks/useBackendError'

import { Title } from '@/components/Title'
import { Back } from '@/components/Back'
import { ChangePasswordForm } from './components/ChangePasswordForm'
import { AccountForm } from './components/AccountForm'
import { Select } from '@/components/form/Select'
import {
  AccessTokenModel,
  RoleUserModel,
} from '@/core/services/Token/model/TokenModel'

import './styles/AccountPage.scss'
import { Button } from '@/components/Button'
import { loginThunk } from '@/core/services/Auth/store/auth.slice'
import { getItem } from '@/utils/persistanceStorage'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { changeRole, setConnectedResellerId } from './store/accont.slice'
import jwtDecode from 'jwt-decode'
import {
  syncCompanyListThunk,
  syncExternalServiceThunk,
  syncResellersThunk,
} from '../LicensesPage/store/licenses.slice'
import { MAX_PAGE_SIZE } from '@/config/constants'

const cnAccount = cn('Account')

export const AccountPage: FC = Memo(() => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const resellers = useAppSelector(resellersSelector)
  const [setBackendError] = useBackendError()
  const navigate = useNavigate()
  const role = useAppSelector(roleSelector)
  const connectedResellerId = useAppSelector(connectedReselleridSelector)
  const userRole = useAppSelector(userRoleSelector)
  const [selectedResellerId, setSelectedResellerId] = useState<
    string | undefined
  >()

  const [isLoadResellers, setLoadResellers] = useState<boolean>(false)
  const [isLoadExternalService, setLoadExternalService] =
    useState<boolean>(false)
  const [isLoadCompanyList, setLoadCompanyList] = useState<boolean>(false)

  const isSyncWithManagerSection =
    role === RoleUserModel.SuperUser || role === RoleUserModel.Reseller
  const isShowConnectReseller =
    userRole === RoleUserModel.SuperUser && !connectedResellerId
  const isShowDisconnectReseller =
    Boolean(connectedResellerId) && userRole === RoleUserModel.SuperUser

  const loadResellers = async () => {
    setLoadResellers(true)
    try {
      await dispatch(
        getResellersThunk({
          join: 'version',
          order: 'desc',
          orderBy: 'ActivationDate',
          pageSize: MAX_PAGE_SIZE,
        }),
      ).unwrap()
    } catch (errors) {
      setBackendError(errors)
    } finally {
      setLoadResellers(false)
    }
  }

  const loadData = useCallback(async () => {
    dispatch(changeLoading(true))
    try {
      dispatch(changeLoading(false))
    } catch (errors) {
      setBackendError(errors)
      dispatch(changeLoading(false))
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (role === RoleUserModel.SuperUser) {
      loadResellers()
    }
  }, [role])

  const handleResellersLicense = async () => {
    setLoadResellers(true)
    try {
      const res = await dispatch(
        syncResellersThunk({
          syncreseller: true,
        }),
      ).unwrap()

      if (res) {
        dispatch(
          setToast({
            type: ToastType.success,
            message: t('You are successfully sync'),
            position: 'top-center',
          }),
        )
        setLoadResellers(false)
      }
    } catch (errors) {
      setLoadResellers(false)
      setBackendError(errors)
    }
  }

  const handleExternalServiceLicense = async () => {
    setLoadExternalService(true)
    try {
      const res = await dispatch(
        syncExternalServiceThunk({
          syncexternalservice: true,
        }),
      ).unwrap()

      if (res) {
        dispatch(
          setToast({
            type: ToastType.success,
            message: t('You are successfully sync'),
            position: 'top-center',
          }),
        )
        setLoadExternalService(false)
      }
    } catch (errors) {
      setLoadExternalService(false)
      setBackendError(errors)
    }
  }

  const handleCompanyList = async () => {
    setLoadCompanyList(true)
    try {
      const res = await dispatch(
        syncCompanyListThunk({
          synclicenses: true,
        }),
      ).unwrap()

      if (res) {
        dispatch(
          setToast({
            type: ToastType.success,
            message: t('You are successfully sync'),
            position: 'top-center',
          }),
        )
        setLoadCompanyList(false)
      }
    } catch (errors) {
      setBackendError(errors)
      setLoadCompanyList(false)
    }
  }

  const handleResellerLogin = async () => {
    try {
      dispatch(changeLoading(true))
      const creds = getItem('creds')
      const res = await dispatch(
        loginThunk({
          username: creds.username,
          password: creds.password,
          reseller: selectedResellerId,
        }),
      ).unwrap()

      if (res.status === 200 && res.data.access_token) {
        const decode: AccessTokenModel = jwtDecode(res.data.access_token)

        dispatch(changeRole(decode.RoleId))
        dispatch(setConnectedResellerId(decode.ResellerId))

        dispatch(
          setToast({
            type: ToastType.success,
            message: t('You are successfully logged in'),
            position: 'top-center',
          }),
        )

        navigate('/cabinet/licenses')
      }
    } catch (errors) {
      setBackendError(errors)
    } finally {
      dispatch(changeLoading(false))
    }
  }

  const handleResellerDisconnect = async () => {
    try {
      dispatch(changeLoading(true))
      const creds = getItem('creds')
      const res = await dispatch(
        loginThunk({
          username: creds.username,
          password: creds.password,
          reseller: null,
        }),
      ).unwrap()

      if (res.status === 200 && res.data.access_token) {
        const decode: AccessTokenModel = jwtDecode(res.data.access_token)
        dispatch(changeRole(decode.RoleId))
        dispatch(setConnectedResellerId(''))

        dispatch(
          setToast({
            type: ToastType.success,
            message: t('You are successfully logged out'),
            position: 'top-center',
          }),
        )

        loadResellers()
      }
    } catch (errors) {
      setBackendError(errors)
    } finally {
      dispatch(changeLoading(false))
    }
  }

  return (
    <>
      <Helmet>
        <title>{t('Account & Billing Information')} | Registration</title>
      </Helmet>
      <div className={cnAccount()}>
        <Back />
        {isShowConnectReseller && (
          <div className={cnAccount('item')}>
            <Title className={cnAccount('title')}>
              {t('Login as Reseller')}
            </Title>
            <Select
              className={cnAccount('list')}
              label={t('Resellers list')}
              placeholder={t('Choose reseller')}
              name='countryCodeId'
              options={
                resellers?.collection.map(item => ({
                  value: item.id,
                  label: item.name,
                })) || []
              }
              optionLabel='label'
              optionValue='value'
              onChange={id => setSelectedResellerId(id as string)}
            />
            <Button onClick={handleResellerLogin} className={cnAccount('btn')}>
              {t('Save')}
            </Button>
          </div>
        )}
        {isShowDisconnectReseller && (
          <div className={cnAccount('item')}>
            <Title className={cnAccount('title')}>{`${t('Connected reseller:', {
              id: connectedResellerId,
            })}`}</Title>
            {/* <Title className={cnAccount('title')}>
              {t('Back to Super User')}
            </Title> */}
            {
              <Button
                onClick={handleResellerDisconnect}
                className={cnAccount('btn')}
              >
                {t('Disconnect')}
              </Button>
            }
          </div>
        )}
        <div className={cnAccount('item')}>
          <Title className={cnAccount('title')}>
            {t('Account & Biling Information')}
          </Title>
          <AccountForm />
        </div>
        <div className={cnAccount('item')}>
          <Title className={cnAccount('title')}>{t('Change Password')}</Title>
          <ChangePasswordForm />
        </div>
        {isSyncWithManagerSection && (
          <div className={cnAccount('item')}>
            <Title className={cnAccount('title')}>
              {t('Sync with manager')}
            </Title>
          </div>
        )}
        {role === RoleUserModel.SuperUser && (
          <div>
            <Button
              onClick={handleResellersLicense}
              className={cnAccount('btn')}
              isLoading={isLoadResellers}
            >
              {t('Sync Resellers')}
            </Button>
            <Button
              onClick={handleExternalServiceLicense}
              className={cnAccount('btn')}
              isLoading={isLoadExternalService}
            >
              {t('Sync External Service')}
            </Button>
          </div>
        )}
        {role === RoleUserModel.Reseller && (
          <div>
            <Button
              onClick={handleCompanyList}
              className={cnAccount('btn')}
              isLoading={isLoadCompanyList}
            >
              {t('Sync Company List')}
            </Button>
          </div>
        )}
        {/* <ResponsiveContainer width='100%' height='100%'>
          <LineChart
            width={500}
            height={300}
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis dataKey='name' />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type='monotone'
              dataKey='pv'
              stroke='#8884d8'
              activeDot={{ r: 8 }}
            />
            <Line type='monotone' dataKey='uv' stroke='#82ca9d' />
          </LineChart>
        </ResponsiveContainer> */}
      </div>
    </>
  )
})
